import _ from 'lodash';
import moment from 'moment-timezone';
import PermissionsUtility from 'go/security/permissionsUtility';
import {isSAR} from 'go/security/permissions';
import REST from 'go/requests/rest';
import {safeAdd} from 'app-ui-componentlibrary/dist/common/utils';
import {
  ACTUAL_PRICE,
  ACTUAL_QUANTITY,
  ACTUAL_TOTAL,
  OBLIGATION_DOC_STATUS_CLOSED,
} from 'common/requestDetails/constants';
import {calcCloseoutReportBudgetShare} from 'common/requestDetails/budgetCalculation';
import {calculatePaymentDispursements} from 'common/utils';
import {safeDivide, safeSubstract} from 'common/utils';
import {calculateManagementCostActualAllowedTotal} from 'common/requestDetails/utils';

const filterSubAppTypes = (props) => {
  const {
    refData,
    applyForOrgId,
    applyToOrgId,
    diffOrgFilteredAppTypes,
    sameOrgFilteredAppTypes,
  } = props;
  if (applyForOrgId === undefined || applyToOrgId === undefined) {
    return [];
  }
  let filterApps = [];
  if (applyForOrgId === applyToOrgId) {
    filterApps = _.filter(refData, (app) => {
      return sameOrgFilteredAppTypes && sameOrgFilteredAppTypes.indexOf(app.id) > -1;
    });
  } else {
    filterApps = _.filter(refData, (app) => {
      return diffOrgFilteredAppTypes && diffOrgFilteredAppTypes.indexOf(app.id) > -1;
    });
  }
  return _.sortBy(filterApps, 'displayText');
};

const filterOrgsByParentOrg = (props) => {
  let allOrgApplyFor = props.refData;
  let orgName = '' + props.applyForOrgId;
  let configId = props.configId;
  let permissions = props.token.permissions;

  let isUsrSAR = isSAR(permissions);

  allOrgApplyFor =
    isUsrSAR && configId == 'rfa'
      ? _.filter(allOrgApplyFor, (org) =>
          _.find(org.parentOrgs, {entityId: org.entityId})
        )
      : _.filter(allOrgApplyFor, (org) => org.entityId == orgName);

  let applyTo = [];
  allOrgApplyFor.map((subOrg) => {
    const subOrgId = subOrg.orgEntityId;
    _.forEach(subOrg.parentOrgs, (org) => {
      const ueiPlusEft =
        org.uei + (!_.isEmpty(_.trim(org.eft)) ? '-' + _.trim(org.eft) : '');
      const uei = org.uei ? ` | UEI-EFT: ${ueiPlusEft}` : '';
      if (org.entityId !== undefined && orgName !== undefined) {
        applyTo.push({
          subOrganizationName: subOrgId,
          organizationId: org.entityId,
          legalName: org.displayName + uei,
        });
      }
    });
    if (
      _.findIndex(subOrg.parentOrgs, (org) => org.entityId === subOrgId) === -1 &&
      subOrg.canHaveSuborgAndSelfSarSM
    ) {
      applyTo.push({
        subOrganizationName: subOrgId,
        organizationId: subOrg.entityId,
        legalName: subOrg.displayName,
      });
    }
  });
  return applyTo;
};

const filterOrgsByProgramByEntity = (props) => {
  const permissionsUtility = new PermissionsUtility(props.token);
  const filterOrgIds = [
    ...permissionsUtility.obj.org,
    ...permissionsUtility.getOrganizationsForProgram(String(props.programId)),
  ];
  const filteredOrgs = _.filter(props.refData, (org) => {
    return _.includes(filterOrgIds, String(org.organizationId));
  });
  return filteredOrgs;
};

const excludeSubAmendmentsByRFIStatus = (props) => {
  // refData should be filterSubAwards, rfis should be rfisForAmendment
  const {refData, rfis, excludeStatuses} = props;
  const excludedRFIs = _.filter(rfis, (r) => _.includes(excludeStatuses, r.status));
  const excludedRFISubGrantIds = _.map(excludedRFIs, (r) => r.subGrantId);
  const filteredSubAmendments = _.filter(
    refData,
    (s) => !_.includes(excludedRFISubGrantIds, s.subGrantId)
  );
  return filteredSubAmendments;
};

const excludeSubAmendmentRecommendationsByIds = (props) => {
  // refData should be filterSubAwards, ids should be a list of subAmendmentRecommendationIds to exclude
  // subGrantIds should be a list of subGrant IDs to exclude
  const {refData, ids, subGrantIds} = props;
  return _.filter(
    refData,
    (subAmendmentRecommendation) =>
      !_.includes(ids, subAmendmentRecommendation.entityId) &&
      !_.includes(subGrantIds, subAmendmentRecommendation.subGrantId)
  );
};

const findSubGrantIdBySubAmendmentRecommendationId = (props) => {
  // refData should be filterSubAwards
  const {refData, subAmendmentRecommendationId} = props;
  const matchingSubAmendmentRecommendation = _.filter(
    refData,
    (s) => s.entityId === subAmendmentRecommendationId
  );
  if (matchingSubAmendmentRecommendation.length) {
    return matchingSubAmendmentRecommendation[0].subGrantId;
  }
  return '';
};

const findApplicationTypeBySubAmendmentRecommendationId = (props) => {
  // refData should be filterSubAwards
  const {refData, subAmendmentRecommendationId} = props;
  const matchingSubAmendmentRecommendation = _.filter(
    refData,
    (s) => s.entityId === subAmendmentRecommendationId
  );
  if (matchingSubAmendmentRecommendation.length) {
    return _.get(matchingSubAmendmentRecommendation[0], 'payload.applicationType');
  }
  return '';
};

const filterActivities = (props) => {
  const isSelfSubApplication = _.isEqual(props.orgId, props.subOrgId);
  const excludePlanOptions = [338, 344];
  const activities = _.chain(props.applicationTypeActivities)
    .filter(
      (obj) => _.toString(obj.applicationTypeId) === _.toString(props.applicationType)
    )
    .head()
    .value();
  const item = _.get(activities, 'items', []);
  const filterChildren = (option) => {
    let filterOptions = [];
    if (!_.isEmpty(option.children)) {
      filterOptions = _.remove([...option.children], (item) => {
        return !_.includes(excludePlanOptions, item);
      });
    }
    return filterOptions;
  };

  if (Number(props.applicationType) === 231 && !isSelfSubApplication) {
    _.each(item, (option) => {
      if (option.itemCode === 308 || option.itemCode === 309) {
        const children = filterChildren(option);
        _.setWith(option, 'children', children);
      }
    });
  }
  return item;
};

const filterNewlyAddedDisasterLocations = (props) => {
  let newSelectedOptions = [...props.selectedOptions];
  _.map(props.selectableOptions, (opt, idx) => {
    if (moment(opt.entryDt, 'YYYY-MM-DD').isAfter(moment(props.prevReviewedDate))) {
      newSelectedOptions.push(_.get(opt, props.optionIdField));
    }
  });
  return _.uniqBy(newSelectedOptions);
};

const postAddOrDeleteSubApp = async (props) => {
  const {context, saveEntitySessionData} = props;
  const amendmentId = _.get(context, 'amendment.entityId');
  const orgId = _.get(context, 'amendment.organizationId');
  const programId = _.get(context, 'amendment.programId');
  const grantId = _.get(context, 'grant.grantId');

  await Promise.all([
    REST.search
      .getAmendmentRecommendationLinksBySubAmendRecParent(amendmentId, orgId, programId)
      .then(REST.handler.handleResponse),
    REST.dynamicData
      .getSubAmendmentRecommendationsForGrant(grantId)
      .then(REST.handler.handleResponse),
  ]).then((promises) => {
    saveEntitySessionData('context.applicationLinkData', {
      ...promises[0],
      loading: false,
    });
    saveEntitySessionData('context.subAmendmentRecommendationsForGrant', {
      payload: {...promises[1]},
      loading: false,
    });
    saveEntitySessionData('context.mergeListenerDone', null);
  });
};

const postDeleteSubApplicationFromPAR = async (props) => {
  const {dataObject, context, saveEntitySessionData} = props;
  const subGrantId = _.get(dataObject, 'subGrantId');
  const activeDeletionList = _.get(
    context,
    'workflowTask.processVariables.activeDeletionList',
    []
  );
  saveEntitySessionData('context.workflowTask.processVariables.activeDeletionList', [
    ...activeDeletionList,
    subGrantId,
  ]);

  const filterSubAwards = _.get(context, 'filterSubAwards');

  const newFilterSubAwards = _.map(filterSubAwards, (f) =>
    f.subGrantId === subGrantId ? {...f, isBeingDeleted: true} : f
  );

  saveEntitySessionData('context.filterSubAwards', newFilterSubAwards);
};

//If a new object needs to be added, please remove let applicationLinkData to the end of the function to a new function call to be called into this
const mergeSubAwardAndLinkData = (props) => {
  let mergeData = [];
  let subRecommendations = props.dependentKeys.includes('subAmendmentRecommendations')
    ? _.get(props, 'subAmendmentRecommendations')
    : _.get(props, 'subAwardRecommendations');
  let applicationLinkData = _.get(props, 'applicationLinkData');
  let rfis = _.get(props, 'rfis');
  const activeRfis = new Set();
  if (rfis) {
    _.forEach(rfis, (rfi) => {
      if (rfi.status === 'pendingSubmission') {
        activeRfis.add(rfi.subGrantId);
      }
    });
  }

  const subGrants = _.get(props, 'subGrants');

  const activeDeletionList = _.get(props, 'activeDeletionList', []);
  const activeExistingSubGrantList = _.get(props, 'activeExistingSubGrantList', []);

  if (subRecommendations && applicationLinkData) {
    _.forEach(applicationLinkData, (child) => {
      _.forEach(subRecommendations, (sub) => {
        if (
          sub.subGrantId === child.subGrantId &&
          (!props.filterStatus || child.payload.reviewStatus === props.filterStatus)
        ) {
          const matchingSubGrant = _.filter(
            subGrants,
            (s) => s.subGrantId == sub.subGrantId
          );
          let subGrantFedShare = 0;
          if (matchingSubGrant) {
            const fedShareContainer = _.get(
              matchingSubGrant[0],
              'payload.budget.share.fedShare',
              []
            );
            subGrantFedShare = _.sum(
              _.map(fedShareContainer, (share) => _.toNumber(share))
            );
          }
          let item = {
            ...sub,
            isRfiActive: activeRfis.has(sub.subGrantId),
            isBeingDeleted: _.includes(activeDeletionList, sub.subGrantId),
            isExistingSubgrant: _.includes(activeExistingSubGrantList, sub.subGrantId),
            subGrantFedShare,
          };
          mergeData.push(item);
        }
      });
    });
  }

  return mergeData;
};

const mergeSubAmendAndLinkData = (props) => {
  let mergeData = [];
  let amendments = _.get(props, 'amendments');
  let subAmendmentRecommendation = _.get(props, 'subAmendmentRecommendation');
  let desiredSubGrantId = _.get(props, 'subGrantId');

  if (amendments && desiredSubGrantId && subAmendmentRecommendation) {
    _.forEach(amendments, (amendment) => {
      let amendmentId = _.get(subAmendmentRecommendation, 'payload.amendmentId');
      if (amendmentId && amendmentId === amendment.entityId) {
        let payload = {};
        payload.entityType = _.get(subAmendmentRecommendation, 'entityType');
        payload.entityNumber = amendment.entityNumber;
        payload.status = amendment.status;
        payload.entityId = _.get(subAmendmentRecommendation, 'entityId');
        payload.statusTimestamp = amendment.statusTimestamp;
        mergeData.push(payload);
      }
    });
  }
  return mergeData;
};

const mergeSubAmendAndLinkDataForMultiple = (props) => {
  let mergeData = [];
  let amendments = _.get(props, 'amendments');
  let subAmendmentRecommendations = _.get(props, 'subAmendmentRecommendations');
  let desiredSubGrantId = _.get(props, 'subGrantId');

  if (amendments && desiredSubGrantId && subAmendmentRecommendations) {
    _.forEach(amendments, (amendment) => {
      _.forEach(subAmendmentRecommendations, (subAmendmentRecommendation) => {
        let amendmentId = _.get(subAmendmentRecommendation, 'payload.amendmentId');
        if (
          amendmentId &&
          amendmentId === amendment.entityId &&
          _.get(subAmendmentRecommendation, 'subGrantId') === desiredSubGrantId
        ) {
          let payload = {};
          payload.entityType = _.get(subAmendmentRecommendation, 'entityType');
          payload.entityNumber = amendment.entityNumber;
          payload.status = amendment.status;
          payload.entityId = _.get(subAmendmentRecommendation, 'entityId');
          payload.statusTimestamp = amendment.statusTimestamp;
          mergeData.push(payload);
        }
      });
    });
  }
  return mergeData;
};

const setDefaultValuesForBsirReportFSCostItemHelper = (item, path) => {
  let costs = _.get(item, 'cost');
  if (costs && costs.length > 0) {
    _.forEach(costs, (costItems, costItemsKey) => {
      _.forEach(costItems, (costItem, costItemKey) => {
        if (!_.has(costItem, ACTUAL_PRICE)) {
          _.set(costItem, ACTUAL_PRICE, '0.00');
        }
        if (!_.has(costItem, ACTUAL_QUANTITY)) {
          _.set(costItem, ACTUAL_QUANTITY, 0);
        }
        if (!_.has(costItem, ACTUAL_TOTAL)) {
          _.set(costItem, ACTUAL_TOTAL, 0);
        }
      });
    });
  }
  _.forEach(item.children, (child, pathKey) => {
    setDefaultValuesForBsirReportFSCostItemHelper(child, [...path, 'children', pathKey]);
  });
};

export const setDefaultValuesForBsirReportFSCostItem = (props) => {
  let fundingStreams = _.get(props, 'bsirReportFundingStreams');
  const cloneFundingStreams = _.cloneDeep(fundingStreams);
  _.forEach(cloneFundingStreams, (stream, streamInd) => {
    _.forEach(stream.children, (child, childKey) => {
      setDefaultValuesForBsirReportFSCostItemHelper(child, [
        streamInd,
        'children',
        childKey,
      ]);
    });
  });
  return cloneFundingStreams;
};

const postCreateRFI = ({saveEntitySessionData, ...lookupData}) => {
  const filterSubAwards = _.get(lookupData, 'context.filterSubAwards');
  const subAmendmentRecommendationId = _.get(lookupData, 'subAmendmentRecommendationId');
  const newFilterSubAwards = _.map(filterSubAwards, (f) =>
    f.entityId === subAmendmentRecommendationId ? {...f, isRfiActive: true} : f
  );
  saveEntitySessionData('context.filterSubAwards', newFilterSubAwards);
};

const buildPoeteListFromItemCode = ({itemCode, refData}) => {
  const item = _.find(refData, (item) => _.get(item, 'itemCode', -1) == itemCode, null);
  if (!item) return [];

  const poeteValues = _.get(item, 'poetes');
  if (_.isEmpty(poeteValues)) return [];

  // build select list options array
  const poeteList = [];
  _.forEach(poeteValues, (val) => {
    let text = _.upperFirst(_.toLower(val));
    if (val === 'MANAGEMENT_AND_ADMINISTRATION') {
      text = 'Management & Administration';
    }
    poeteList.push({key: val, value: val, text});
  });
  return poeteList;
};

const getLatestBsirReport = (props) => {
  let data = [];
  let bsirReports = _.get(props, 'bsirReports');
  let financialReportEndDate = _.get(props, 'financialReportEndDate');
  let expectedPath = _.get(props, 'expectedPath');

  if (bsirReports && financialReportEndDate) {
    let latestBsirReport = _.find(
      bsirReports,
      (report) => _.get(report, expectedPath) === financialReportEndDate
    );
    if (latestBsirReport) {
      let actualTotalAmount = _.get(
        latestBsirReport,
        'payload.budget.actualTotalAmount',
        0
      );
      let tempDocument = _.cloneDeep(latestBsirReport);
      _.set(
        tempDocument,
        'payload.budget.actualTotalAmount',
        parseFloat(actualTotalAmount).toFixed(2)
      );
      data.push(tempDocument);
    }
  }
  return data;
};

const filterDonatedResourcesSubapps = (props) => {
  let subApplications = _.get(props, 'subApplications', []);
  const limitedSubAppType = '674';
  const applyForOrgId = _.get(props, 'applyForOrgId');
  subApplications = _.filter(subApplications, (subapp) => {
    return (
      _.get(subapp, 'subOrganizationId') == applyForOrgId &&
      _.get(subapp, 'applicationType') == limitedSubAppType
    );
  });
  return subApplications;
};

const filterSubCloseoutReportBudgetShare = (props) => {
  let share = _.get(props, 'share', null);
  let savedShare = _.get(props, 'savedShare', null);

  if (savedShare) return savedShare;
  return {
    ...share,
    actualAppRate: share.appRate,
    actualFedRate: share.fedRate,
    actualAppShare: share.appShare,
    actualFedShare: share.fedShare,
  };
};

const filterSubCloseoutReportTotal = (props) => {
  let total = _.get(props, 'total');
  let savedTotal = _.get(props, 'savedTotal');

  if (savedTotal) return savedTotal;
  return total;
};

const filterObligationsByStatus = (props) => {
  let obligations = _.get(props, 'obligations');
  return obligations.filter(
    (obligation) => obligation.status != OBLIGATION_DOC_STATUS_CLOSED
  );
};

const calculateCloseoutReportBudgetShare = (props) => {
  const {grantBudget, foConfig, fundingStreams, share} = props;
  if (_.get(share, 'actualFedShare')) {
    return share;
  }
  return calcCloseoutReportBudgetShare({
    grantBudget,
    foConfig,
    fundingStreams,
  });
};

const calculateCloseoutReportBudgetShareAndCopyBudgetSummary = (props) => {
  const {
    grantBudget,
    foConfig,
    fundingStreams,
    share,
    budgetSummary,
    closeoutType = 'closeoutReport',
    budget = {},
  } = props;
  let updatedShare = calculateCloseoutReportBudgetShare(props);
  let budgetSummaryToUse = _.cloneDeep(
    budgetSummary ? budgetSummary : _.get(grantBudget, 'budgetSummary')
  );
  const totalChargesField = _.isEqual(closeoutType, 'closeoutReport')
    ? 'actualTotalCharges'
    : 'actualAllowedTotalCharges';
  if (!budgetSummaryToUse[totalChargesField]) {
    budgetSummaryToUse[totalChargesField] = budgetSummaryToUse.totalCharges;
  }
  return {
    ...budget,
    budgetSummary: budgetSummaryToUse,
    share: updatedShare,
  };
};

const calculateSumOfFields = (entityList, path) => {
  const totalAmount = entityList.reduce((acc, entity) => {
    const field = _.get(entity, path);

    const sum = _.isArray(field)
      ? safeAdd(..._.map(field, _.toNumber))
      : _.toNumber(field);

    return safeAdd(acc, sum);
  }, 0);

  return totalAmount;
};

const calculateSumOfFieldsAtSpecificIndex = (entityList, path) => {
  const totalAmount = entityList.reduce((acc, entity) => {
    const field = _.get(entity, path);

    return safeAdd(acc, field);
  }, 0);

  return totalAmount;
};

const calculateSumOfManagementCosts = (entityList, subGrantsList) => {
  const totalAmount = entityList.reduce((acc, entity) => {
    const subGrantId = entity.subGrantId;
    const subGrant = _.find(
      subGrantsList,
      (subGrant) => subGrant.subGrantId === subGrantId
    );
    const subGrantType = _.get(subGrant, 'payload.applicationType');

    if (subGrantType == '233') {
      return acc;
    }
    const fundingStream = _.get(entity, 'payload.fundingStreams');
    const sum = calculateManagementCostActualAllowedTotal(fundingStream);

    return safeAdd(acc, sum);
  }, 0);

  return totalAmount;
};

const calculateAwardCostsForPassthroughCloseoutReport = (props) => {
  const {
    subGrantsList,
    subCloseoutRecommendationsList,
    calculateManagementCostsForBRICFMAFY20andFY21,
    budget = {},
  } = props;

  const subGrantAwardCosts =
    calculateSubGrantAwardCostsForPassthroughCloseoutReport(props);

  const grandTotal = calculateSumOfFields(
    subCloseoutRecommendationsList,
    'payload.budget.totalAmount'
  );

  const actualGrandTotal = calculateSumOfFields(
    subCloseoutRecommendationsList,
    'payload.budget.actualAllowedTotalAmount'
  );

  const actualFedShare = calculateSumOfFields(
    subCloseoutRecommendationsList,
    'payload.budget.share.actualFedShare'
  );

  const actualNonFederalShare = safeSubstract(grandTotal, actualFedShare);
  const actualNonFederalSharePercentage = _.round(
    _.multiply(_.divide(actualNonFederalShare, grandTotal), 100),
    2
  );
  const actualFederalSharePercentage = safeSubstract(
    100,
    actualNonFederalSharePercentage
  );

  let actualSubRecipientManagementCosts = 0;

  if (!props.calculateManagementCostsForBRICFMAFY20andFY21) {
    actualSubRecipientManagementCosts = calculateSumOfFieldsAtSpecificIndex(
      subCloseoutRecommendationsList,
      'payload.budget.share.totalAmounts.1'
    );
  } else {
    actualSubRecipientManagementCosts = calculateSumOfManagementCosts(
      subCloseoutRecommendationsList,
      subGrantsList
    );
  }

  const actualRecipientManagementCosts = calculateSumOfFields(
    _.filter(subCloseoutRecommendationsList, (entity) => {
      const subGrantId = entity.subGrantId;
      const subGrant = _.find(
        subGrantsList,
        (subGrant) => subGrant.subGrantId === subGrantId
      );
      const subGrantType = _.get(subGrant, 'payload.applicationType');

      return subGrantType == '233';
    }),
    'payload.budget.actualAllowedTotalAmount'
  );

  const actualNonManagementCosts = safeSubstract(
    actualGrandTotal,
    actualSubRecipientManagementCosts,
    actualRecipientManagementCosts
  );

  return {
    awardCosts: {
      grandTotal,
      actualGrandTotal,
      actualFedShare,
      actualNonFederalShare,
      actualNonFederalSharePercentage,
      actualFederalSharePercentage,
      actualSubRecipientManagementCosts,
      actualRecipientManagementCosts,
      actualNonManagementCosts,
    },
    subGrantAwardCosts,
  };
};

const calculateSubGrantAwardCostsForPassthroughCloseoutReport = (props) => {
  const {
    subGrantsList,
    subCloseoutRecommendationsList,
    payments,
    subGrantObligationAmounts,
  } = props;

  const subGrantAwardCosts = _.map(
    subCloseoutRecommendationsList,
    (subCloseoutRecommendation) => {
      return calculateSubGrantAwardCosts(
        subCloseoutRecommendation,
        subGrantsList,
        payments,
        subGrantObligationAmounts
      );
    }
  );

  return subGrantAwardCosts;
};

const calculateSubGrantAwardCosts = (
  subCloseoutRecommendation,
  subGrantsList,
  payments,
  subGrantObligationAmounts
) => {
  const subGrantId = subCloseoutRecommendation.subGrantId;
  const subGrant = _.find(
    subGrantsList,
    (subGrant) => subGrant.subGrantId === subGrantId
  );

  const fedShare = safeAdd(
    ...subCloseoutRecommendation.payload.budget.share.actualFedShare
  );
  const appShare = safeAdd(
    ...subCloseoutRecommendation.payload.budget.share.actualAppShare
  );

  const paymentsForSubgrant = _.filter(
    payments,
    (payment) => payment.subGrantId === subGrantId
  );
  const totalDisbursed =
    calculatePaymentDispursements(paymentsForSubgrant).federalDispursements;

  const nonManagementCostFedShare = _.get(
    subCloseoutRecommendation,
    'payload.budget.share.actualFedShare.0'
  );
  const nonManagementCostAppShare = _.get(
    subCloseoutRecommendation,
    'payload.budget.share.actualAppShare.0'
  );
  const nonManagementCostTotal = safeAdd(
    nonManagementCostFedShare,
    nonManagementCostAppShare
  );

  const managementCostFedShare = _.get(
    subCloseoutRecommendation,
    'payload.budget.share.actualFedShare.1',
    0
  );
  const managementCostAppShare = _.get(
    subCloseoutRecommendation,
    'payload.budget.share.actualAppShare.1',
    0
  );
  const managementCostTotal = safeAdd(managementCostFedShare, managementCostAppShare);

  const obligationAmounts = _.get(subGrantObligationAmounts, subGrantId);
  const totalAvailableAmount = _.get(obligationAmounts, 'totalAvailableAmount');
  let amountToBeReturned = safeAdd(fedShare, -1 * totalDisbursed, totalAvailableAmount);

  return {
    subGrantId,
    applicationTypeDescription: subGrant.payload.applicationTypeDescription,
    applicationType: subGrant.payload.applicationType,
    actualAllowedTotalAmount:
      subCloseoutRecommendation.payload.budget.actualAllowedTotalAmount,
    actualFedShare: fedShare,
    actualAppShare: appShare,
    dateClosed: subCloseoutRecommendation.payload.closeoutDate,
    totalDisbursed,
    nonManagementCostFedShare,
    nonManagementCostAppShare,
    nonManagementCostTotal,
    managementCostTotal,
    totalAvailableAmount,
    amountToBeReturned: amountToBeReturned < 0 ? Math.abs(amountToBeReturned) : 0,
  };
};

const initiateCloseoutReportData = (props) => {
  const {
    grantBudget,
    foConfig,
    fundingStreams,
    grantFundingStreams,
    share,
    budgetSummary,
    payload,
    closeoutType,
    budget = {},
    fieldMap = {
      quantity: 'actualQuantity',
      price: 'actualPrice',
      total: 'actualTotal',
    },
  } = props;
  let fundingStreamsToUse = fundingStreams;
  if (_.isEmpty(fundingStreams)) {
    fundingStreamsToUse = _.cloneDeep(grantFundingStreams);
    fundingStreamsToUse = _.map(fundingStreamsToUse, (fundingStreamItem) => {
      let {fundingStreamItem: updatedFundingStreamItem} = setNestedCostItems(
        fundingStreamItem,
        fieldMap
      );
      return updatedFundingStreamItem;
    });
  }
  let updatedShare = calculateCloseoutReportBudgetShare({
    ..._.cloneDeep(props),
    fundingStreams: fundingStreamsToUse,
  });
  let budgetSummaryToUse = _.cloneDeep(
    budgetSummary ? budgetSummary : _.get(grantBudget, 'budgetSummary')
  );
  const totalChargesField = _.isEqual(closeoutType, 'closeoutReport')
    ? 'actualTotalCharges'
    : 'actualAllowedTotalCharges';
  if (
    !budgetSummaryToUse[totalChargesField] &&
    _.isEqual(closeoutType, 'closeoutRecommendation')
  ) {
    budgetSummaryToUse[totalChargesField] = budgetSummaryToUse.actualTotalCharges;
  } else if (!budgetSummaryToUse[totalChargesField]) {
    budgetSummaryToUse[totalChargesField] = budgetSummaryToUse.totalCharges;
  }
  const budgetToUse = {
    ...budget,
    budgetSummary: budgetSummaryToUse,
    share: updatedShare,
  };

  return {
    ...payload,
    budget: budgetToUse,
    fundingStreams: fundingStreamsToUse,
  };
};

const setNestedCostItems = (fundingStreamItem, fieldMap) => {
  let changed = false;
  if (fundingStreamItem.children) {
    let updatedChildren = _.cloneDeep(fundingStreamItem.children);
    _.forEach(fundingStreamItem.children, (child, key) => {
      let {fundingStreamItem: updatedChild, changed: childChanged} = setNestedCostItems(
        child,
        fieldMap
      );
      changed = changed || childChanged;
      if (childChanged) _.set(updatedChildren, key, updatedChild);
    });
    _.set(fundingStreamItem, 'children', updatedChildren);
  } else if (fundingStreamItem.cost) {
    const costArray = fundingStreamItem.cost;
    const updatedArray = _.map(costArray, (costItems) => {
      return _.map(costItems, (costItem) => {
        const updatedCostItem = _.cloneDeep(costItem);
        _.forEach(fieldMap, (destVal, sourceVal) => {
          if (
            updatedCostItem[sourceVal] !== undefined &&
            updatedCostItem[destVal] === undefined
          ) {
            _.set(updatedCostItem, destVal, updatedCostItem[sourceVal]);
            changed = true;
          }
        });
        return updatedCostItem;
      });
    });
    _.set(fundingStreamItem, 'cost', updatedArray);
  }
  return {fundingStreamItem, changed};
};

const filterSubCloseoutReportBudgetSummary = (props) => {
  let nonFederal = _.get(props, 'nonFederal', null);
  let savedNonFederal = _.get(props, 'savedNonFederal', null);

  if (savedNonFederal) return savedNonFederal;
  return {
    ...nonFederal,
  };
};

const filterSubgrantsWithRealProperty = (props) => {
  const {subCloseoutReportsForGrant, subGrantsForGrant, subOrganizationsForGrant} = props;

  let subGrantsToUse = _.cloneDeep(subGrantsForGrant);
  let subOrgsToUse = _.cloneDeep(subOrganizationsForGrant.payload);

  let subCloseoutsWithRealProperty = _.filter(
    subCloseoutReportsForGrant.payload,
    (subCloseout) => {
      if (!subCloseout.payload.realProperty.reportType.notApplicable) return subCloseout;
    }
  );

  let subGrantsToShow = !_.isEmpty(subCloseoutsWithRealProperty)
    ? _.filter(subGrantsToUse.payload, (subGrant) => {
        for (let i = 0; i < subCloseoutsWithRealProperty.length; i++) {
          if (
            _.isEqual(subCloseoutsWithRealProperty[i].subGrantId, subGrant.subGrantId)
          ) {
            subGrant.subCloseoutId = subCloseoutsWithRealProperty[i].entityId;
            for (let j = 0; j < subOrgsToUse.length; j++) {
              if (subOrgsToUse[j].organizationId == subGrant.organizationId) {
                subGrant.subOrgName = subOrgsToUse[j].displayName;
              }
            }
            return subGrant;
          }
        }
      })
    : [];

  return {
    subGrantsToReport: subGrantsToShow,
  };
};

const filterSubGrantsByTypeAndSumAppShare = (props) => {
  const {allSubGrantsOrgSubOrg, applicationTypes} = props;
  const returnObj = {
    appShare: 0,
  };
  _.reduce(
    _.get(allSubGrantsOrgSubOrg, 'items', []),
    (res, subGrant) => {
      const applicationType = _.get(subGrant, 'payload.applicationType', 'X');
      if (_.includes(applicationTypes, applicationType)) {
        const appShareArr = _.get(subGrant, 'payload.budget.share.appShare', []);
        returnObj.appShare = safeAdd(...appShareArr, returnObj.appShare);
      }
    },
    returnObj
  );
  return returnObj;
};

const matchEhpReviewToSubAmendment = (props) => {
  const {ehpReviewsForSubGrant, subAmendmentRecommendationsForSubGrant} = props;
  if (ehpReviewsForSubGrant.loading) {
    return {loading: true};
  }
  let returnData = _.map(
    _.filter(ehpReviewsForSubGrant, (y) => y),
    (review) => {
      //the URL is stored under requestDetail, will point us to preAward or amendment EHP review
      const row = {
        sourceLabel: 'Award',
        entryDate: review.entryDate,
        reviewStatus: review.reviewStatus,
        modifyDate: review.modifyDate,
        entityId: review.reviewId,
      };
      if (_.includes(review.requestDetail, 'subAmendmentRecommendation')) {
        const parts = _.split(_.get(review, 'requestDetail'), '/');
        const subAmendmentRecommendationId = _.get(
          parts,
          _.indexOf(parts, 'subAmendmentRecommendation') + 1
        );

        const entityNumber = _.get(
          _.find(subAmendmentRecommendationsForSubGrant, [
            'entityId',
            subAmendmentRecommendationId,
          ]),
          'entityNumber',
          0
        );
        row.sourceLabel = `Amendment ${entityNumber}`;
      }
      return row;
    }
  );
  returnData = _.orderBy(returnData, 'modifyDate', 'desc');
  returnData = _.uniqBy(returnData, 'sourceLabel');
  return returnData;
};

const retrieveAssociatedEntityForEhpReview = (props) => {
  const {ehpReviewsForSubGrant, reviewData} = props;
  const review = _.find(ehpReviewsForSubGrant, (review) => {
    if (review.reviewId == reviewData.reviewStatus.reviewId) return true;
  });
  if (review && _.includes(review.requestDetail, 'subAmendmentRecommendation')) {
    const parts = _.split(_.get(review, 'requestDetail'), '/');
    const subAmendmentRecommendationId = _.get(
      parts,
      _.indexOf(parts, 'subAmendmentRecommendation') + 1
    );
    return {
      entityType: 'subAmendmentRecommendation',
      entityId: subAmendmentRecommendationId,
    };
  } else {
    return {
      entityType: 'subAwardRecommendation',
    };
  }
};
const mergeOrganizationAndFundingOpportunityOrganization = (props) => {
  let mergeData = [];
  let organization = _.get(props, 'orgDetails', []);
  let fundingOpportunityOrganization = _.get(props, 'foOrgsDetailsList', []);
  let orgDetails = _.filter(organization, (value) => _.isObject(value));
  let fundingOrgs = _.filter(fundingOpportunityOrganization, (value) =>
    _.isObject(value)
  );
  if (orgDetails.length && fundingOrgs.length) {
    _.forEach(orgDetails, (org) => {
      const matchingOrg = _.find(fundingOrgs, {organizationId: org.organizationId});
      if (matchingOrg) {
        mergeData.push({
          organizationId: org.organizationId,
          displayName: org.displayName,
          organizationType: org.organizationType,
          dba: org.doingBusinessAs,
          legalName: org.legalBusinessName,
          duns: org.duns,
          dunsPlus4: org.eft,
          agreementType: matchingOrg.recipientAgreementType,
          uei: org.uei,
          physicalAddress: org.physicalAddress,
        });
      }
    });
  }
  return mergeData;
};

const mergeRecipientsAndAgreements = (props) => {
  let mergeData = [];
  let orgDetailList = _.get(props, 'orgDetailList', []);
  let orgAgreementList = _.get(props, 'orgAgreementList', []);
  let orgDetails = _.filter(orgDetailList, (value) => _.isObject(value));
  let orgAgreements = _.filter(orgAgreementList, (value) => _.isObject(value));

  if (orgDetails.length && orgAgreements.length) {
    const foOrgMap = _.keyBy(orgAgreements, 'key');
    mergeData = _.reduce(
      orgDetails,
      (acc, org) => {
        const matchingOrg = foOrgMap[org.organizationId];
        if (matchingOrg) {
          acc.push({...org, agreementType: matchingOrg.agreementType});
        }
        return acc;
      },
      []
    );
  }
  return mergeData;
};

const unlockRecipients = (props) => {
  const now = new Date();
  let unlocked = false;
  const programId = _.get(props, 'programId');

  if (programId == 47) {
    const declareDate = _.get(props, 'disasterDeclarationDate');
    if (declareDate) {
      const lockDate = new Date(declareDate);
      lockDate.setMonth(lockDate.getMonth() + 9);
      unlocked = lockDate >= now;
    }
  }

  if (programId == 16 || programId == 17) {
    const appEndDate = _.get(props, 'applicationEndDate');
    const appEndTime = _.get(props, 'applicationEndTime', '00:00');
    const lockDate = appEndDate ? new Date(`${appEndDate} ${appEndTime}`) : now;

    unlocked = lockDate >= now;
  }

  return unlocked;
};

export default {
  filterActivities,
  filterOrgsByProgramByEntity,
  filterOrgsByParentOrg,
  filterSubAppTypes,
  filterNewlyAddedDisasterLocations,
  filterSubGrantsByTypeAndSumAppShare,
  mergeSubAwardAndLinkData,
  mergeSubAmendAndLinkData,
  mergeSubAmendAndLinkDataForMultiple,
  setDefaultValuesForBsirReportFSCostItem,
  excludeSubAmendmentsByRFIStatus,
  excludeSubAmendmentRecommendationsByIds,
  findSubGrantIdBySubAmendmentRecommendationId,
  findApplicationTypeBySubAmendmentRecommendationId,
  postDeleteSubApplicationFromPAR,
  postCreateRFI,
  postAddOrDeleteSubApp,
  buildPoeteListFromItemCode,
  getLatestBsirReport,
  filterDonatedResourcesSubapps,
  filterSubCloseoutReportBudgetShare,
  filterSubCloseoutReportBudgetSummary,
  matchEhpReviewToSubAmendment,
  retrieveAssociatedEntityForEhpReview,
  mergeOrganizationAndFundingOpportunityOrganization,
  mergeRecipientsAndAgreements,
  unlockRecipients,
  calculateCloseoutReportBudgetShare,
  filterSubCloseoutReportTotal,
  calculateCloseoutReportBudgetShareAndCopyBudgetSummary,
  calculateAwardCostsForPassthroughCloseoutReport,
  initiateCloseoutReportData,
  filterObligationsByStatus,
  filterSubgrantsWithRealProperty,
  calculateSumOfManagementCosts,
};
